(() => {
  'use strict';

  if (window.__drpenxEventsV2) return;
  window.__drpenxEventsV2 = true;

  const CONTRACT_VERSION = '2.0.0';
  const ALLOWED_EVENTS = new Set([
    'view_product',
    'variant_change',
    'add_to_cart',
    'view_cart',
    'begin_checkout',
    'collection_filter_select',
    'gallery_change',
    'search_submit'
  ]);
  const pending = [];
  const recent = new Map();

  function analyticsAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    }
    catch (_error) {
      return false;
    }
  }

  function compactObject(value) {
    return Object.fromEntries(Object.entries(value).filter(([, field]) => field !== undefined && field !== null && field !== ''));
  }

  function dedupeKey(eventName, payload) {
    if (eventName === 'view_product') return `${eventName}:${payload.product_id}`;
    if (eventName === 'view_cart') return `${eventName}:${payload.source_component}:${payload.item_count}`;
    if (eventName === 'variant_change') return `${eventName}:${payload.variant_id}`;
    if (eventName === 'gallery_change') return `${eventName}:${payload.media_id}:${payload.input_mode}`;
    if (eventName === 'add_to_cart') return `${eventName}:${payload.variant_id}:${payload.quantity}:${payload.source_component}`;
    return `${eventName}:${JSON.stringify(payload)}`;
  }

  function deliver(eventName, payload) {
    const eventPayload = compactObject({
      event: eventName,
      event_contract_version: CONTRACT_VERSION,
      event_timestamp: new Date().toISOString(),
      ...payload
    });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventPayload);
    if (document.body) {
      document.body.dataset.drxAnalyticsConsent = 'allowed';
      document.body.dataset.drxEventLast = eventName;
      document.body.dataset.drxEventCount = String(toNumber(document.body.dataset.drxEventCount) + 1);
      const sequence = (document.body.dataset.drxEventSequence || '').split(',').filter(Boolean);
      sequence.push(eventName);
      document.body.dataset.drxEventSequence = sequence.slice(-12).join(',');
    }
    document.dispatchEvent(new CustomEvent('drpenx:event', { detail: eventPayload }));
  }

  function emit(eventName, payload, options = {}) {
    if (!ALLOWED_EVENTS.has(eventName)) return;
    const normalized = compactObject(payload || {});
    const key = dedupeKey(eventName, normalized);
    const now = Date.now();
    const lifetime = options.once ? Number.POSITIVE_INFINITY : (options.dedupeMs || 900);
    const last = recent.get(key);
    if (last !== undefined && now - last < lifetime) return;
    recent.set(key, now);

    if (!analyticsAllowed()) {
      if (pending.length < 24) pending.push([eventName, normalized, options]);
      if (document.body) {
        document.body.dataset.drxAnalyticsConsent = 'blocked';
        document.body.dataset.drxEventQueued = String(pending.length);
      }
      return;
    }
    deliver(eventName, normalized);
  }

  function flushPending() {
    if (!analyticsAllowed()) return;
    if (document.body) document.body.dataset.drxEventQueued = '0';
    pending.splice(0).forEach(([eventName, payload]) => deliver(eventName, payload));
  }

  function toNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function pageFamily(body) {
    if (body.dataset.drxPageType !== 'product') return body.dataset.drxPageType || 'unknown';
    const handle = body.dataset.drxProductHandle || '';
    const type = (body.dataset.drxProductType || '').toLowerCase();
    if (handle.includes('cartridges') || type.includes('cartridge')) return 'PF-PDP-CARTRIDGE-001';
    if (type.includes('stamp') || handle.includes('needle-h')) return 'PF-PDP-MANUAL-001';
    return 'PF-PDP-DEVICE-001';
  }

  function sourceComponent(element) {
    if (element?.closest?.('.product-sticky-form, product-sticky-form')) return 'sticky_purchase';
    if (element?.closest?.('[data-drx-card], [data-dxp-card], [data-dxoffer-card], .product-card')) return 'product_card';
    if (element?.closest?.('#CartDrawer, .cart-drawer')) return 'cart_drawer';
    if (element?.closest?.('.dx-cart, [data-dx-cart-page]')) return 'cart_page';
    return 'pdp_primary';
  }

  function cartMix(root = document) {
    const productLinks = Array.from(root.querySelectorAll('a[href*="/products/"]'));
    if (!productLinks.length) return 'unknown';
    const cartridgeCount = productLinks.filter((link) => link.getAttribute('href')?.includes('cartridges')).length;
    if (cartridgeCount === productLinks.length) return 'cartridge';
    if (cartridgeCount === 0) return 'device';
    return 'mixed';
  }

  function cartSessionReference() {
    const storageKey = 'drpenx_cart_session_ref';
    try {
      let value = sessionStorage.getItem(storageKey);
      if (!value) {
        value = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(storageKey, value);
      }
      return value;
    }
    catch (_error) {
      return 'session-unavailable';
    }
  }

  function currentResultCount(body = document.body) {
    const selected = document.querySelector('[role="tab"][aria-selected="true"]');
    if (selected?.dataset.count) return toNumber(selected.dataset.count);
    const explicit = document.querySelector('[data-result-count], [data-results-count]');
    const explicitValue = explicit?.dataset.resultCount || explicit?.dataset.resultsCount;
    if (explicitValue !== undefined) return toNumber(explicitValue);
    const visibleCards = Array.from(document.querySelectorAll('[data-drx-card], [data-dxp-card], [data-dxoffer-card]'))
      .filter((card) => !card.hidden && getComputedStyle(card).display !== 'none');
    return visibleCards.length || toNumber(body.dataset.drxResultCount);
  }

  function selectedFilter(button) {
    return button.dataset.dxpFamily || button.dataset.dxcartFamily || button.dataset.dxofferFamily || button.dataset.dxconcernTarget || button.dataset.family || 'all';
  }

  function knownSearchIntent(rawQuery) {
    const query = String(rawQuery || '').trim().toLowerCase().slice(0, 80);
    if (/\b(m8|m7s)\b/.test(query) && /(cartridge|needle|pin|nano)/.test(query)) return 'find_m8_cartridge';
    if (/\b(m8|dr\.?\s*pen\s*m8)\b/.test(query)) return 'find_m8_device';
    if (/\b(h3|h5|hydra\s*pen)\b/.test(query)) return 'find_hydra_device';
    if (/\b(q2|bio\s*pen)\b/.test(query)) return 'find_bio_device';
    if (/\b(hn20|hn25|hn30|h24|manual|stamp)\b/.test(query)) return 'find_manual_tool';
    if (/(compatible|compatibility|which.*fit)/.test(query)) return 'resolve_compatibility';
    return 'unclassified';
  }

  function safeSearchLabel(rawQuery) {
    const intent = knownSearchIntent(rawQuery);
    return intent === 'unclassified' ? 'unclassified_query' : intent;
  }

  function mediaRole(media, index) {
    const explicit = media?.dataset?.mediaRole;
    if (explicit) return explicit;
    if (index === 0) return 'identity';
    const text = `${media?.getAttribute?.('aria-label') || ''} ${media?.querySelector?.('img')?.alt || ''}`.toLowerCase();
    if (/(compatib|connector|fit)/.test(text)) return 'compatibility';
    if (/(pack|included|box|case)/.test(text)) return 'pack';
    if (/(depth|speed|control|display|adjust)/.test(text)) return 'control';
    if (/(routine|skin|scalp|face|hand)/.test(text)) return 'routine';
    return 'system';
  }

  function initPageView(body) {
    if (body.dataset.drxPageType === 'product') {
      emit('view_product', {
        product_id: body.dataset.drxProductId,
        product_handle: body.dataset.drxProductHandle,
        page_family: pageFamily(body)
      }, { once: true });
    }

    if (body.dataset.drxPageType === 'cart') {
      emit('view_cart', {
        cart_token_hash: cartSessionReference(),
        item_count: toNumber(body.dataset.drxCartCount),
        cart_mix: cartMix(document),
        source_component: 'cart_page'
      }, { once: true });
    }

    if (body.dataset.drxPageType === 'search') {
      const query = new URLSearchParams(location.search).get('q') || '';
      emit('search_submit', {
        query_normalized: safeSearchLabel(query),
        matched_intent: knownSearchIntent(query),
        result_count: currentResultCount(body)
      }, { once: true });
    }
  }

  function bindThemeEvents(body) {
    document.querySelectorAll('media-gallery, [data-cx-canvas], .product__gallery').forEach((gallery) => {
      if (gallery.dataset.drxGalleryObserver === 'true') return;
      gallery.dataset.drxGalleryObserver = 'true';
      const controls = Array.from(gallery.querySelectorAll('[data-media-id], [data-cx-gallery-control]'));
      const stateControls = controls.some((control) => control.hasAttribute('aria-current'))
        ? controls.filter((control) => control.hasAttribute('aria-current'))
        : controls;
      const selectedControl = () => stateControls.find((control) => control.getAttribute('aria-current') === 'true' || control.classList.contains('is-selected'));
      let selectedMediaId = selectedControl()?.dataset.mediaId || selectedControl()?.dataset.cxGalleryControl || '';
      let reportTimer;

      const reportSelectedMedia = () => {
        const control = selectedControl();
        if (!control) return;
        const mediaId = control.dataset.mediaId || control.dataset.cxGalleryControl || '';
        if (!mediaId || mediaId === selectedMediaId) return;
        selectedMediaId = mediaId;
        const index = Math.max(0, stateControls.indexOf(control));
        emit('gallery_change', {
          product_id: body.dataset.drxProductId,
          media_id: String(mediaId),
          media_role: mediaRole(control, index),
          input_mode: 'control'
        }, { dedupeMs: 5000 });
      };

      const queueSelectedMedia = () => {
        clearTimeout(reportTimer);
        reportTimer = setTimeout(reportSelectedMedia, 750);
      };

      new MutationObserver(queueSelectedMedia).observe(gallery, {
        attributes: true,
        subtree: true,
        attributeFilter: ['aria-current', 'class']
      });
    });

    document.addEventListener('variant:change', (event) => {
      const variant = event.detail?.variant || {};
      emit('variant_change', {
        product_id: body.dataset.drxProductId,
        variant_id: String(variant.id || ''),
        availability: Boolean(variant.available)
      });
    });

    document.addEventListener('ajaxProduct:added', (event) => {
      const product = event.detail?.product || {};
      const form = document.activeElement?.closest?.('form[is="product-form"], form[action*="/cart/add"]');
      const formData = form ? new FormData(form) : null;
      emit('add_to_cart', {
        product_id: String(product.product_id || body.dataset.drxProductId || ''),
        variant_id: String(product.variant_id || product.id || formData?.get('id') || ''),
        quantity: toNumber(product.quantity || formData?.get('quantity'), 1),
        source_component: sourceComponent(form || document.activeElement)
      });
    });

    document.addEventListener('click', (event) => {
      const filter = event.target.closest('[data-dxp-family], [data-dxcart-family], [data-dxoffer-family], [data-dxconcern-target]');
      if (filter) {
        setTimeout(() => emit('collection_filter_select', {
          collection_handle: body.dataset.drxCollectionHandle || location.pathname.split('/').filter(Boolean).pop() || 'unknown',
          filter_id: selectedFilter(filter),
          result_count: currentResultCount(body)
        }), 180);
      }

      const checkout = event.target.closest('[name="checkout"], [data-checkout], a[href*="/checkout"]');
      if (checkout) {
        emit('begin_checkout', {
          item_count: toNumber(body.dataset.drxCartCount),
          cart_value: toNumber(body.dataset.drxCartValue),
          currency: body.dataset.drxCurrency || 'USD',
          source_component: sourceComponent(checkout)
        });
      }
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/search')) return;
      const query = new FormData(form).get('q') || '';
      try {
        sessionStorage.setItem('drpenx_pending_search_intent', safeSearchLabel(query));
      }
      catch (_error) {
        // Search continues normally when storage is unavailable.
      }
    }, true);
  }

  function bindCartDrawer(body) {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    let wasVisible = false;

    const observe = () => {
      const visible = !drawer.hidden && drawer.getAttribute('aria-hidden') !== 'true';
      if (visible && !wasVisible) {
        emit('view_cart', {
          cart_token_hash: cartSessionReference(),
          item_count: toNumber(body.dataset.drxCartCount),
          cart_mix: cartMix(drawer),
          source_component: 'cart_drawer'
        });
      }
      wasVisible = visible;
    };

    new MutationObserver(observe).observe(drawer, { attributes: true, attributeFilter: ['hidden', 'open', 'aria-hidden', 'class'] });
    observe();
  }

  function init() {
    const body = document.body;
    if (!body) return;
    body.dataset.drxEventsReady = 'true';
    body.dataset.drxEventCount = body.dataset.drxEventCount || '0';
    body.dataset.drxEventSequence = body.dataset.drxEventSequence || '';
    body.dataset.drxAnalyticsConsent = analyticsAllowed() ? 'allowed' : 'blocked';
    initPageView(body);
    bindThemeEvents(body);
    bindCartDrawer(body);
    document.addEventListener('visitorConsentCollected', flushPending);
    window.addEventListener('pageshow', flushPending);
    setTimeout(flushPending, 500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
